import { type DateRange, addDays, daysBetween, rangesOverlap } from '@room-mates/shared';
import { useQueries } from '@tanstack/react-query';
import { type ReactElement, useState } from 'react';
import { formatDate, todayIso } from '../../shared/format.js';
import { queryKeys } from '../../shared/api/queryKeys.js';
import { useApi } from '../../shared/auth/useAuth.js';
import { bedKindLabel } from '../../shared/i18n/dictionary.js';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorAlert,
  LoadingBlock,
  PageHeader,
} from '../../shared/ui/index.js';
import { useAllBeds } from '../houses/houseHooks.js';
import {
  DateRangeCalendar,
  type PartialDateRange,
  UNBOUNDED_RANGE,
} from '../sojourns/DateRangeCalendar.js';
import { useSojourns } from '../sojourns/sojournHooks.js';
import {
  groupByHouseAndRoom,
  type NamedPlan,
  occupantsByBed,
  unassignedGuests,
} from './calendarOccupancy.js';

export function CalendarPage(): ReactElement {
  const t = useTranslation();
  const api = useApi();
  const beds = useAllBeds();
  const sojourns = useSojourns();
  const [selection, setSelection] = useState<PartialDateRange>(() => ({
    start: todayIso(),
    end: null,
  }));
  // DateRangeCalendar only reads `value.start` to pick its initial visible
  // month; jumping back to today has to force a remount, or the grid would
  // keep showing whatever month the organiser had navigated away to.
  const [calendarResetKey, setCalendarResetKey] = useState(0);

  const effectiveRange: DateRange = {
    start: selection.start ?? todayIso(),
    end: selection.end ?? addDays(selection.start ?? todayIso(), 1),
  };
  const caption =
    selection.end === null
      ? t.calendarPage.showingSingle(formatDate(effectiveRange.start))
      : t.calendarPage.showingRange(
          formatDate(effectiveRange.start),
          formatDate(effectiveRange.end),
          t.count.nights(daysBetween(effectiveRange.start, effectiveRange.end)),
        );

  // Only planned sojourns can place anyone; an unplanned one that overlaps the
  // window contributes nothing but is not an error either.
  const plannedOverlapping = (sojourns.data ?? []).filter(
    (sojourn) =>
      sojourn.hasPlan &&
      rangesOverlap({ start: sojourn.startDate, end: sojourn.endDate }, effectiveRange),
  );

  const planQueries = useQueries({
    queries: plannedOverlapping.map((sojourn) => ({
      queryKey: queryKeys.sojourns.plan(sojourn.id),
      queryFn: () => api.plans.get(sojourn.id),
    })),
  });

  const namedPlans: NamedPlan[] = plannedOverlapping.flatMap((sojourn, index) => {
    const plan = planQueries[index]?.data;
    return plan === undefined ? [] : [{ sojournId: sojourn.id, sojournName: sojourn.name, plan }];
  });
  const plansLoading = planQueries.some((query) => query.isPending);
  const planError = planQueries.find((query) => query.error)?.error ?? null;

  const houses = groupByHouseAndRoom(beds.data ?? []);
  const occupancy = occupantsByBed(namedPlans, effectiveRange);
  const unassigned = unassignedGuests(namedPlans, effectiveRange);

  const totalBeds = beds.data?.length ?? 0;
  const occupiedBeds = [...occupancy.values()].filter((occupants) => occupants.length > 0).length;
  const conflictBeds = [...occupancy.values()].filter((occupants) => occupants.length > 1).length;
  const showSojournName = plannedOverlapping.length > 1;

  return (
    <div className="stack stack--loose">
      <PageHeader title={t.calendarPage.title} subtitle={t.calendarPage.subtitle} />

      <Card>
        <div className="stack">
          <DateRangeCalendar
            key={calendarResetKey}
            bounds={UNBOUNDED_RANGE}
            value={selection}
            caption={caption}
            onChange={setSelection}
          />
          <div className="row row--end">
            <Button
              small
              variant="secondary"
              onClick={() => {
                setSelection({ start: todayIso(), end: null });
                setCalendarResetKey((count) => count + 1);
              }}
            >
              {t.common.todayButton}
            </Button>
          </div>
        </div>
      </Card>

      <ErrorAlert error={beds.error ?? sojourns.error ?? planError} />

      {beds.isPending || sojourns.isPending || plansLoading ? (
        <LoadingBlock label={t.calendarPage.loadingOccupancy} />
      ) : null}

      {beds.data?.length === 0 ? (
        <EmptyState
          title={t.calendarPage.noBedsTitle}
          description={t.calendarPage.noBedsDescription}
        />
      ) : null}

      {houses.length > 0 ? (
        <>
          <p className="card__meta">
            {t.calendarPage.bedsOccupiedOf(t.count.beds(occupiedBeds), totalBeds)}
            {conflictBeds > 0 ? ` · ${t.count.doubleBookedBeds(conflictBeds)}` : ''}
          </p>

          {houses.map((house) => (
            <Card key={house.houseId}>
              <div className="stack">
                <h2>{house.houseName}</h2>
                {house.rooms.map((room) => (
                  <div key={room.roomId} className="stack stack--tight">
                    <h3>{room.roomName}</h3>
                    <ul className="data-list">
                      {room.beds.map((bed) => {
                        const occupants = occupancy.get(bed.id) ?? [];
                        return (
                          <li key={bed.id} className="list-row">
                            <span className="list-row__title" title={bed.name}>
                              {bedKindLabel(t, bed.type, bed.option)}
                            </span>
                            {occupants.length === 0 ? (
                              <Badge tone="positive">{t.calendarPage.free}</Badge>
                            ) : (
                              <div className="option-list">
                                {occupants.map((occupant) => (
                                  <Badge
                                    key={`${occupant.sojournId}:${occupant.guestId}`}
                                    tone={occupants.length > 1 ? 'danger' : 'accent'}
                                  >
                                    {occupant.guestName}
                                    {showSojournName ? ` · ${occupant.sojournName}` : ''}
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </>
      ) : null}

      {unassigned.length > 0 ? (
        <Card>
          <div className="stack stack--tight">
            <h2>{t.calendarPage.withoutABedTitle}</h2>
            <p className="card__meta">{t.calendarPage.withoutABedDescription}</p>
            <ul className="data-list">
              {unassigned.map((guest) => (
                <li key={`${guest.sojournId}:${guest.guestId}`} className="list-row">
                  <span className="list-row__title">{guest.guestName}</span>
                  <span className="list-row__meta">
                    {guest.sojournName} · {guest.message}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
